/*
 * BONIFAKTUR custom wishlist — client module.
 *
 * Source of truth for guests = localStorage["bf_wishlist"] (the FULL structure,
 * same shape the Worker stores). For logged-in customers it's an optimistic
 * cache: changes apply locally + to the UI instantly, then a debounced
 * PUT /apps/wishlist syncs the whole structure through the App Proxy. The theme
 * never touches the metafield shape directly — only the Worker does.
 *
 * Structure: { v:1, lists:[ { id, name, system?, items:[ {vid,h,added_at} ] } ] }
 * A variant is "saved" if it's in ANY list. Items are variant-level; the
 * wishlist page groups them by product handle at render time (presentation only).
 */
(function () {
  'use strict';

  var PROXY = '/apps/wishlist';
  var STORAGE_KEY = 'bf_wishlist';
  var SYNCED_FLAG = 'bf_wishlist_synced';
  var COLLAPSE_KEY = 'bf_wishlist_collapse';
  var DEFAULT_ID = 'default';
  var DEFAULT_NAME = bfT('wl_default_name', 'Merkliste');
  var MAX_LISTS = 50;

  var customerId = (typeof window.__bfCustomer !== 'undefined' && window.__bfCustomer) ? String(window.__bfCustomer) : null;
  var loggedIn = !!customerId;

  // ---- Anonymous guest analytics ----------------------------------------
  // Guest wishlists live only in localStorage, so the server never sees them.
  // To understand not-logged-in usage we fire a tiny anonymous beacon on each
  // guest add/remove — a random session id only, NO PII. Logged-in users are
  // skipped (their wishlist already syncs to the account). Goes through the App
  // Proxy (same-origin, signed); body is text/plain to avoid any preflight.
  var GUEST_SID_KEY = 'bf_wishlist_sid';
  function guestSid() {
    try {
      var s = localStorage.getItem(GUEST_SID_KEY);
      if (!s || !/^[A-Za-z0-9_-]{6,40}$/.test(s)) {
        s = (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 24);
        localStorage.setItem(GUEST_SID_KEY, s);
      }
      return s;
    } catch (e) { return 'anonwlsess00'; }
  }
  function trackGuest(action, vid, handle) {
    if (loggedIn || !vid) return;
    try {
      var body = JSON.stringify({ sid: guestSid(), events: [{ vid: Number(vid), handle: handle || '', action: action, ts: Date.now() }] });
      var url = PROXY + '/track';
      var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      var ok = navigator.sendBeacon && navigator.sendBeacon(url, blob);
      if (!ok) fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: body, keepalive: true });
    } catch (e) { /* analytics must never break the wishlist */ }
  }

  // ---- State -------------------------------------------------------------

  function defaultStructure() {
    return { v: 1, lists: [{ id: DEFAULT_ID, name: DEFAULT_NAME, system: true, items: [] }] };
  }

  function loadState() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { raw = null; }
    if (!raw || !Array.isArray(raw.lists)) return defaultStructure();
    // Guarantee a default list at the front.
    var hasDefault = raw.lists.some(function (l) { return l && l.id === DEFAULT_ID; });
    if (!hasDefault) raw.lists.unshift({ id: DEFAULT_ID, name: DEFAULT_NAME, system: true, items: [] });
    raw.v = 1;
    return raw;
  }

  function saveState(state) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* quota — ignore */ }
  }

  function allSavedVids(state) {
    var set = new Set();
    state.lists.forEach(function (l) { (l.items || []).forEach(function (it) { set.add(Number(it.vid)); }); });
    return set;
  }

  function isSaved(state, vid) {
    vid = Number(vid);
    return state.lists.some(function (l) { return (l.items || []).some(function (it) { return Number(it.vid) === vid; }); });
  }

  function listById(state, id) {
    return state.lists.filter(function (l) { return l.id === id; })[0] || null;
  }

  function today() { return new Date().toISOString().slice(0, 10); }

  function genListId() {
    return 'p_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
  }

  function addToList(state, listId, vid, handle, kit) {
    var list = listById(state, listId);
    if (!list) return;
    vid = Number(vid);
    if (list.items.some(function (it) { return Number(it.vid) === vid; })) return;
    var item = { vid: vid, h: handle || '', added_at: today() };
    if (kit) item.k = 1;
    list.items.push(item);
  }

  function removeFromList(state, listId, vid) {
    var list = listById(state, listId);
    if (!list) return;
    vid = Number(vid);
    list.items = list.items.filter(function (it) { return Number(it.vid) !== vid; });
  }

  // Per-item note (scoped to the item within a given list).
  function setItemNote(state, listId, vid, note) {
    var list = listById(state, listId);
    if (!list) return;
    vid = Number(vid);
    var item = (list.items || []).filter(function (it) { return Number(it.vid) === vid; })[0];
    if (!item) return;
    if (note) item.note = String(note).slice(0, 500);
    else delete item.note;
  }

  // Per-list description / project brief.
  function setListDesc(state, listId, desc) {
    var list = listById(state, listId);
    if (!list) return;
    if (desc) list.desc = String(desc).slice(0, 200);
    else delete list.desc;
  }

  function removeEverywhere(state, vid) {
    vid = Number(vid);
    state.lists.forEach(function (l) {
      l.items = (l.items || []).filter(function (it) { return Number(it.vid) !== vid; });
    });
  }

  // Resolve the handle for a vid from any list (used when copying to a project).
  function handleForVid(state, vid) {
    vid = Number(vid);
    for (var i = 0; i < state.lists.length; i++) {
      var hit = (state.lists[i].items || []).filter(function (it) { return Number(it.vid) === vid; })[0];
      if (hit) return hit.h || '';
    }
    return '';
  }

  // Is the saved item a knitting-kit scaffold? (preserve the flag when copying)
  function itemIsKit(state, vid) {
    vid = Number(vid);
    for (var i = 0; i < state.lists.length; i++) {
      var hit = (state.lists[i].items || []).filter(function (it) { return Number(it.vid) === vid; })[0];
      if (hit) return !!hit.k;
    }
    return false;
  }

  // ---- Server sync (logged-in only) -------------------------------------

  var pushTimer = null;
  var pushSerial = 0;

  function schedulePush() {
    if (!loggedIn) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(doPush, 400);
  }

  function doPush() {
    if (!loggedIn) return;
    var serial = ++pushSerial;
    var state = loadState();
    fetch(PROXY, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ data: state })
    }).then(function (res) {
      if (!res.ok) return null;
      return res.json();
    }).then(function (body) {
      // Adopt the canonical server copy only if no newer local change happened
      // since this push started (else a later push will reconcile instead).
      if (body && body.data && serial === pushSerial) {
        saveState(body.data);
        refreshControls();
      }
    }).catch(function () { /* offline / 401 → keep local */ });
  }

  function getServer() {
    return fetch(PROXY, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (b) { return b && b.data ? b.data : null; })
      .catch(function () { return null; });
  }

  function postMerge(guestState) {
    return fetch(PROXY + '/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ data: guestState })
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (b) { return b && b.data ? b.data : null; })
      .catch(function () { return null; });
  }

  // ---- Heart hydration + badge ------------------------------------------

  function refreshControls() {
    var state = loadState();
    var saved = allSavedVids(state);
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var on = saved.has(Number(btn.dataset.vid));
      setHeart(btn, on);
    });
    paintBadge(saved.size);
  }

  function setHeart(btn, on) {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? bfT('wl_remove_aria', 'Gemerkt — entfernen') : bfT('save_yarn', 'Garn merken'));
    btn.setAttribute('title', on ? bfT('wl_saved_title', 'Gemerkt') : bfT('save_yarn', 'Garn merken'));
    var label = btn.querySelector('[data-wishlist-label]');
    if (label) label.textContent = on ? bfT('wl_saved_label', 'gemerkt') : bfT('save_short', 'merken');
  }

  function paintBadge(count) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      if (count > 0) {
        el.textContent = '(' + count + ')';
        el.hidden = false;
        el.classList.add('is-bump');
        setTimeout(function () { el.classList.remove('is-bump'); }, 320);
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
  }

  function reflectVid(vid, on) {
    vid = Number(vid);
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      if (Number(btn.dataset.vid) === vid) {
        setHeart(btn, on);
        if (on) {
          btn.classList.add('is-pop');
          setTimeout(function () { btn.classList.remove('is-pop'); }, 320);
        }
      }
    });
  }

  // ---- Toast -------------------------------------------------------------

  function showToast(message, actions) {
    var host = document.querySelector('[data-wishlist-toast]');
    if (!host) {
      host = document.createElement('div');
      host.className = 'wishlist-toast';
      host.setAttribute('data-wishlist-toast', '');
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
    }
    host.innerHTML = '';
    var msg = document.createElement('span');
    msg.className = 'wishlist-toast__msg';
    msg.textContent = message;
    host.appendChild(msg);
    (actions || []).forEach(function (a) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wishlist-toast__action';
      b.textContent = a.label;
      b.addEventListener('click', function () { hideToast(host); a.onClick(); });
      host.appendChild(b);
    });
    host.classList.add('is-visible');
    clearTimeout(host._t);
    host._t = setTimeout(function () { hideToast(host); }, 5000);
  }

  function hideToast(host) {
    host = host || document.querySelector('[data-wishlist-toast]');
    if (host) host.classList.remove('is-visible');
  }

  // ---- Heart toggle ------------------------------------------------------

  function toggleSave(btn) {
    var vid = Number(btn.dataset.vid);
    var handle = btn.dataset.handle || '';
    var isKit = btn.dataset.kind === 'kit';
    if (!vid) return;
    var state = loadState();
    var saved = isSaved(state, vid);

    if (saved) {
      var wasKit = itemIsKit(state, vid) || isKit;
      removeEverywhere(state, vid);
      saveState(state);
      reflectVid(vid, false);
      paintBadge(allSavedVids(state).size);
      schedulePush();
      trackGuest('remove', vid, handle);
      showToast(bfT('wl_removed_toast', 'Aus der Wunschliste entfernt'), [{
        label: 'Rückgängig',
        onClick: function () {
          var s = loadState();
          addToList(s, DEFAULT_ID, vid, handle, wasKit);
          saveState(s);
          reflectVid(vid, true);
          paintBadge(allSavedVids(s).size);
          schedulePush();
          if (isOnWishlistPage()) renderPage();
        }
      }]);
    } else {
      addToList(state, DEFAULT_ID, vid, handle, isKit);
      saveState(state);
      reflectVid(vid, true);
      paintBadge(allSavedVids(state).size);
      schedulePush();
      trackGuest('add', vid, handle);
      var actions = [{
        label: 'Rückgängig',
        onClick: function () {
          var s = loadState();
          removeEverywhere(s, vid);
          saveState(s);
          reflectVid(vid, false);
          paintBadge(allSavedVids(s).size);
          schedulePush();
          if (isOnWishlistPage()) renderPage();
        }
      }];
      if (loggedIn) {
        actions.unshift({ label: bfT('wl_add_to_project', 'In Projekt legen'), onClick: function () { openProjectPicker(vid, handle); } });
      }
      showToast(bfT('wl_saved_toast', 'Gemerkt ✓'), actions);
    }
    if (isOnWishlistPage()) renderPage();
  }

  // ---- Project picker (logged-in) ---------------------------------------
  // Membership model: a tickable list per project. Checking adds the variant to
  // that list, unchecking removes it — which also covers "copy"/"move".

  function openProjectPicker(vid, handle) {
    closePicker();
    var state = loadState();
    handle = handle || handleForVid(state, vid);

    var back = document.createElement('div');
    back.className = 'wishlist-picker__backdrop';
    back.setAttribute('data-wishlist-picker', '');

    var box = document.createElement('div');
    box.className = 'wishlist-picker';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-label', bfT('wl_add_to_project', 'In Projekt legen'));

    var title = document.createElement('p');
    title.className = 'wishlist-picker__title';
    title.textContent = bfT('wl_add_to_project', 'In Projekt legen');
    box.appendChild(title);

    state.lists.forEach(function (l) {
      var row = document.createElement('label');
      row.className = 'wishlist-picker__row';
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = (l.items || []).some(function (it) { return Number(it.vid) === Number(vid); });
      cb.addEventListener('change', function () {
        var s = loadState();
        if (cb.checked) addToList(s, l.id, vid, handle, itemIsKit(s, vid));
        else removeFromList(s, l.id, vid);
        saveState(s);
        reflectVid(vid, isSaved(s, vid));
        paintBadge(allSavedVids(s).size);
        schedulePush();
        if (isOnWishlistPage()) renderPage();
      });
      var span = document.createElement('span');
      span.textContent = l.name + (l.system ? '' : '');
      row.appendChild(cb);
      row.appendChild(span);
      box.appendChild(row);
    });

    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'wishlist-picker__new';
    add.textContent = bfT('wl_new_list', '+ Neue Liste');
    add.addEventListener('click', function () {
      var keepVid = vid, keepHandle = handle;
      openPrompt({
        title: bfT('wl_new_list_title', 'Neue Liste'), placeholder: bfT('wl_new_list_ph1', 'z. B. Sockengarn-Ideen'), maxlength: 60, confirmLabel: bfT('create', 'Erstellen'),
        onConfirm: function (name) {
          if (!name) return;
          var s = loadState();
          if (s.lists.length >= MAX_LISTS) { showToast(bfT('max_lists', 'Maximale Anzahl an Listen erreicht.'), []); return; }
          var id = genListId();
          s.lists.push({ id: id, name: name.slice(0, 60), items: [] });
          addToList(s, id, keepVid, keepHandle, itemIsKit(s, keepVid));
          saveState(s);
          reflectVid(keepVid, true);
          paintBadge(allSavedVids(s).size);
          schedulePush();
          if (isOnWishlistPage()) renderPage();
        }
      });
    });
    box.appendChild(add);

    var done = document.createElement('button');
    done.type = 'button';
    done.className = 'wishlist-picker__done';
    done.textContent = 'Fertig';
    done.addEventListener('click', closePicker);
    box.appendChild(done);

    back.appendChild(box);
    back.addEventListener('click', function (e) { if (e.target === back) closePicker(); });
    document.body.appendChild(back);
  }

  function closePicker() {
    var p = document.querySelector('[data-wishlist-picker]');
    if (p) p.remove();
  }

  // ---- In-page dialogs (replace native prompt/confirm) -------------------

  function onDialogKeydown(e) {
    if (e.key === 'Escape') closeDialog();
  }

  function closeDialog() {
    var d = document.querySelector('[data-wishlist-dialog]');
    if (d) d.remove();
    document.removeEventListener('keydown', onDialogKeydown);
  }

  function mkBtn(text, cls, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = text;
    b.addEventListener('click', onClick);
    return b;
  }

  function buildDialogShell(titleText) {
    closeDialog();
    closePicker();
    var back = document.createElement('div');
    back.className = 'wishlist-dialog__backdrop';
    back.setAttribute('data-wishlist-dialog', '');
    var box = document.createElement('div');
    box.className = 'wishlist-dialog';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    if (titleText) {
      var title = document.createElement('p');
      title.className = 'wishlist-dialog__title';
      title.textContent = titleText;
      box.appendChild(title);
    }
    back.appendChild(box);
    back.addEventListener('click', function (e) { if (e.target === back) closeDialog(); });
    document.addEventListener('keydown', onDialogKeydown);
    document.body.appendChild(back);
    return box;
  }

  // opts: { title, value, placeholder, multiline, maxlength, confirmLabel, onConfirm(value) }
  function openPrompt(opts) {
    var box = buildDialogShell(opts.title);
    var field = document.createElement(opts.multiline ? 'textarea' : 'input');
    field.className = 'wishlist-dialog__input';
    if (!opts.multiline) field.type = 'text';
    else field.rows = 3;
    field.value = opts.value || '';
    if (opts.placeholder) field.placeholder = opts.placeholder;
    if (opts.maxlength) field.maxLength = opts.maxlength;
    box.appendChild(field);

    var actions = document.createElement('div');
    actions.className = 'wishlist-dialog__actions';
    var save = mkBtn(opts.confirmLabel || 'Speichern', 'wishlist-dialog__btn wishlist-dialog__btn--primary', function () {
      var v = field.value.trim();
      closeDialog();
      opts.onConfirm(v);
    });
    actions.appendChild(mkBtn('Abbrechen', 'wishlist-dialog__btn wishlist-dialog__btn--ghost', closeDialog));
    actions.appendChild(save);
    box.appendChild(actions);

    field.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !opts.multiline) { e.preventDefault(); save.click(); }
    });
    setTimeout(function () { field.focus(); if (field.select) field.select(); }, 20);
  }

  // opts: { title, message, confirmLabel, danger, onConfirm() }
  function openConfirm(opts) {
    var box = buildDialogShell(opts.title);
    if (opts.message) {
      var msg = document.createElement('p');
      msg.className = 'wishlist-dialog__msg';
      msg.textContent = opts.message;
      box.appendChild(msg);
    }
    var actions = document.createElement('div');
    actions.className = 'wishlist-dialog__actions';
    var ok = mkBtn(opts.confirmLabel || 'OK', 'wishlist-dialog__btn wishlist-dialog__btn--primary' + (opts.danger ? ' wishlist-dialog__btn--danger' : ''), function () {
      closeDialog();
      opts.onConfirm();
    });
    actions.appendChild(mkBtn('Abbrechen', 'wishlist-dialog__btn wishlist-dialog__btn--ghost', closeDialog));
    actions.appendChild(ok);
    box.appendChild(actions);
    setTimeout(function () { ok.focus(); }, 20);
  }

  // ---- Wishlist page -----------------------------------------------------

  var activeListId = DEFAULT_ID;
  var productCache = {};

  function isOnWishlistPage() { return !!document.querySelector('[data-wishlist-root]'); }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  function getConfig() {
    var el = document.getElementById('wishlist-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function lowStockThreshold() {
    var cfg = getConfig();
    return (cfg && typeof cfg.lowStock === 'number') ? cfg.lowStock : 5;
  }

  // Live stock counts via the Storefront API (one batch call). Returns a
  // vid → quantityAvailable map. Degrades to {} if the token lacks inventory
  // scope or anything fails → rows fall back to plain "Auf Lager".
  function fetchQuantities(vids) {
    var cfg = getConfig();
    if (!cfg || !cfg.sfToken || !cfg.shop || !vids.length) return Promise.resolve({});
    var ids = vids.map(function (v) { return 'gid://shopify/ProductVariant/' + v; });
    var query = 'query($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id quantityAvailable}}}';
    return fetch('https://' + cfg.shop + '/api/' + (cfg.sfVersion || '2024-10') + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.sfToken },
      body: JSON.stringify({ query: query, variables: { ids: ids } })
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        var map = {};
        var nodes = body && body.data && body.data.nodes;
        if (Array.isArray(nodes)) {
          nodes.forEach(function (n) {
            if (n && n.id && typeof n.quantityAvailable === 'number') {
              map[Number(n.id.replace('gid://shopify/ProductVariant/', ''))] = n.quantityAvailable;
            }
          });
        }
        return map;
      }).catch(function () { return {}; });
  }

  function money(cents) {
    try {
      return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  // Per-item note control: shows the note (click to edit) or a "+ Notiz" prompt.
  function noteControlHtml(vid, note) {
    if (note) {
      return '<button type="button" class="wishlist-row__note" data-wishlist-note data-vid="' + vid + '"><span class="wishlist-row__note-text"></span></button>';
    }
    return '<button type="button" class="wishlist-row__note wishlist-row__note--add" data-wishlist-note data-vid="' + vid + '">+ Notiz</button>';
  }

  function sizedImg(url, w) {
    if (!url) return '';
    var clean = url.split('?')[0];
    return clean + '?width=' + w;
  }

  function getCollapse() {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function setCollapse(map) {
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(map)); } catch (e) { /* ignore */ }
  }

  function renderTabs(state) {
    var bar = document.querySelector('[data-wishlist-bar]');
    var tabs = document.querySelector('[data-wishlist-tabs]');
    if (!tabs) return;
    if (!listById(state, activeListId)) activeListId = DEFAULT_ID;

    // Projects are a logged-in feature → guests get no tab bar (default list only).
    if (!loggedIn) {
      if (bar) bar.hidden = true;
      return;
    }
    if (bar) bar.hidden = false;

    tabs.innerHTML = '';
    state.lists.forEach(function (l) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wishlist-tab' + (l.id === activeListId ? ' is-active' : '');
      btn.setAttribute('data-wishlist-tab', l.id);
      btn.setAttribute('aria-pressed', l.id === activeListId ? 'true' : 'false');
      btn.innerHTML = '<span class="wishlist-tab__name"></span><span class="wishlist-tab__count">' + (l.items || []).length + '</span>';
      btn.querySelector('.wishlist-tab__name').textContent = l.name;
      tabs.appendChild(btn);
    });

    if (loggedIn) {
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'wishlist-tab wishlist-tab--new';
      add.setAttribute('data-wishlist-newlist', '');
      add.textContent = bfT('wl_new_list', '+ Neue Liste');
      tabs.appendChild(add);
    }
  }

  function renderPage() {
    var root = document.querySelector('[data-wishlist-root]');
    if (!root) return;
    var body = root.querySelector('[data-wishlist-body]');
    var state = loadState();

    renderTabs(state);
    renderNudge(root, state);
    renderListDesc(root, state);

    var list = listById(state, activeListId) || listById(state, DEFAULT_ID);
    var items = (list && list.items) ? list.items.slice() : [];

    // Rename/delete only for a logged-in customer's own (non-system) list.
    var canManage = loggedIn && list && !list.system;
    var renameBtn = root.querySelector('[data-wishlist-rename]');
    var deleteBtn = root.querySelector('[data-wishlist-delete]');
    if (renameBtn) renameBtn.hidden = !canManage;
    if (deleteBtn) deleteBtn.hidden = !canManage;

    if (!items.length) {
      body.innerHTML = '';
      body.appendChild(emptyState());
      return;
    }

    body.innerHTML = '<p class="wishlist__loading t-mono">' + bfT('loading', 'Wird geladen …') + '</p>';

    Promise.all(items.map(function (it) {
      return fetchProduct(it.h).then(function (p) { return { it: it, p: p }; });
    })).then(function (rows) {
      var instockOnly = !!(root.querySelector('[data-wishlist-instock]') && root.querySelector('[data-wishlist-instock]').checked);
      var collapse = getCollapse();

      // Build per-product groups (presentation only). A kit group (scaffold)
      // renders a "Zum Kit" link and never an add-to-cart.
      var groups = {};
      var order = [];
      rows.forEach(function (row) {
        var p = row.p, it = row.it;
        if (!p) return; // deleted/unavailable product → skip on render
        var variant = (p.variants || []).filter(function (v) { return Number(v.id) === Number(it.vid); })[0];
        if (!variant) return; // deleted variant → skip
        if (!groups[p.handle]) {
          groups[p.handle] = { product: p, rows: [], isKit: !!it.k };
          order.push(p.handle);
        }
        if (it.k) groups[p.handle].isKit = true;
        groups[p.handle].rows.push({ variant: variant, added_at: it.added_at, note: it.note });
      });

      if (!order.length) {
        body.innerHTML = '';
        body.appendChild(emptyState());
        return;
      }

      // One Storefront batch call for live counts (non-kit variants only).
      var qVids = [];
      order.forEach(function (h) {
        if (!groups[h].isKit) groups[h].rows.forEach(function (r) { qVids.push(r.variant.id); });
      });

      fetchQuantities(qVids).then(function (qtyMap) {
        var frag = document.createDocumentFragment();
        order.forEach(function (handle) {
          var g = groups[handle];
          if (g.isKit) {
            frag.appendChild(renderKitGroup(g.product, g.rows, collapse));
            return;
          }
          // sold-out colors sort last; recency otherwise
          g.rows.sort(function (a, b) {
            if (a.variant.available !== b.variant.available) return a.variant.available ? -1 : 1;
            return String(b.added_at || '').localeCompare(String(a.added_at || ''));
          });
          var visibleRows = instockOnly ? g.rows.filter(function (r) { return r.variant.available; }) : g.rows;
          if (!visibleRows.length) return;
          frag.appendChild(renderGroup(g.product, visibleRows, collapse, qtyMap));
        });

        body.innerHTML = '';
        if (!frag.childNodes.length) {
          body.appendChild(emptyState(true));
        } else {
          body.appendChild(frag);
        }
      });
    });
  }

  function renderGroup(product, rows, collapse, qtyMap) {
    var total = rows.length;
    var inStock = rows.filter(function (r) { return r.variant.available; }).length;
    var collapsed = !!collapse[product.handle];

    var wrap = document.createElement('article');
    wrap.className = 'wishlist-group' + (collapsed ? ' is-collapsed' : '');
    wrap.setAttribute('data-wishlist-group', product.handle);

    var head = document.createElement('button');
    head.type = 'button';
    head.className = 'wishlist-group__head';
    head.setAttribute('data-wishlist-group-toggle', product.handle);
    head.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    var summary = (total === 1 ? bfT('wl_summary_one', '{total} Farbe · {stock} auf Lager') : bfT('wl_summary_other', '{total} Farben · {stock} auf Lager')).replace('{total}', total).replace('{stock}', inStock);
    head.innerHTML =
      '<span class="wishlist-group__title"></span>' +
      '<span class="wishlist-group__summary t-mono">' + summary + '</span>' +
      '<span class="wishlist-group__chevron" aria-hidden="true">›</span>';
    head.querySelector('.wishlist-group__title').textContent = product.title;
    wrap.appendChild(head);

    var listEl = document.createElement('div');
    listEl.className = 'wishlist-group__rows';
    rows.forEach(function (r) { listEl.appendChild(renderRow(product, r.variant, qtyMap, r.note)); });
    wrap.appendChild(listEl);
    return wrap;
  }

  // Kit group — scaffold product. No stock/price/ATC; a "Zum Kit" link routes
  // back to the kit page where the actual yarns get configured + added.
  function renderKitGroup(product, rows, collapse) {
    var collapsed = !!collapse[product.handle];
    var wrap = document.createElement('article');
    wrap.className = 'wishlist-group wishlist-group--kit' + (collapsed ? ' is-collapsed' : '');
    wrap.setAttribute('data-wishlist-group', product.handle);
    var listEl = document.createElement('div');
    listEl.className = 'wishlist-group__rows';
    // Dedupe by vid (a kit rarely has multiple saved variants).
    var seen = {};
    rows.forEach(function (r) {
      if (seen[r.variant.id]) return;
      seen[r.variant.id] = 1;
      listEl.appendChild(renderKitRow(product, r.variant, r.note));
    });
    wrap.appendChild(listEl);
    return wrap;
  }

  function renderKitRow(product, variant, note) {
    var row = document.createElement('div');
    row.className = 'wishlist-row wishlist-row--kit';
    var imgUrl = variant.featured_image && variant.featured_image.src
      ? variant.featured_image.src
      : (typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image && product.featured_image.src) || '');
    var url = '/products/' + product.handle;

    var swatch = imgUrl
      ? '<a class="wishlist-row__swatch" href="' + url + '"><img loading="lazy" src="' + sizedImg(imgUrl, 120) + '" alt=""></a>'
      : '<span class="wishlist-row__swatch wishlist-row__swatch--empty"></span>';

    row.innerHTML =
      swatch +
      '<div class="wishlist-row__body">' +
        '<a class="wishlist-row__name" href="' + url + '"></a>' +
        '<span class="wishlist-row__kit-tag t-mono">Strick-Kit</span>' +
        noteControlHtml(variant.id, note) +
      '</div>' +
      '<div class="wishlist-row__actions">' +
        '<a class="wishlist-row__atc btn btn--primary" href="' + url + '">Zum Kit</a>' +
        '<button type="button" class="wishlist-row__icon" data-wishlist-remove data-vid="' + variant.id + '" aria-label="' + bfT('remove', 'Entfernen') + '" title="' + bfT('remove', 'Entfernen') + '">♥</button>' +
        (loggedIn ? '<button type="button" class="wishlist-row__icon" data-wishlist-project data-vid="' + variant.id + '" data-handle="' + product.handle + '" aria-label="' + bfT('wl_add_to_project', 'In Projekt legen') + '" title="' + bfT('wl_add_to_project', 'In Projekt legen') + '">⋯</button>' : '') +
      '</div>';
    row.querySelector('.wishlist-row__name').textContent = product.title;
    if (note) { var nt = row.querySelector('.wishlist-row__note-text'); if (nt) nt.textContent = note; }
    return row;
  }

  function renderRow(product, variant, qtyMap, note) {
    var row = document.createElement('div');
    row.className = 'wishlist-row' + (variant.available ? '' : ' is-soldout');
    var imgUrl = variant.featured_image && variant.featured_image.src
      ? variant.featured_image.src
      : (typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image && product.featured_image.src) || '');
    var url = '/products/' + product.handle + '?variant=' + variant.id;

    var swatch = imgUrl
      ? '<a class="wishlist-row__swatch" href="' + url + '"><img loading="lazy" src="' + sizedImg(imgUrl, 120) + '" alt=""></a>'
      : '<span class="wishlist-row__swatch wishlist-row__swatch--empty"></span>';

    // Low-stock hint: exact "Nur noch N" under the threshold, else "Auf Lager".
    var qty = qtyMap ? qtyMap[variant.id] : undefined;
    var stock;
    if (!variant.available) {
      stock = '<span class="wishlist-row__stock is-out">' + bfT('sold_out', 'Ausverkauft') + '</span>';
    } else if (typeof qty === 'number' && qty > 0 && qty <= lowStockThreshold()) {
      stock = '<span class="wishlist-row__stock is-low">' + bfT('only_n_left', 'Nur noch %%N%%').replace(' verfügbar', '').replace('%%N%%', qty) + '</span>';
    } else {
      stock = '<span class="wishlist-row__stock is-in">' + bfT('in_stock_plain', 'Auf Lager') + '</span>';
    }

    var action = variant.available
      ? '<button type="button" class="wishlist-row__atc btn btn--primary" data-wishlist-add data-vid="' + variant.id + '">' + bfT('add_to_cart', 'In den Warenkorb') + '</button>'
      // Phase-2 hook: inert "Benachrichtigen" slot for Klaviyo back-in-stock.
      : '<button type="button" class="wishlist-row__notify" data-wishlist-notify disabled>' + bfT('notify', 'Benachrichtigen') + '</button>';

    row.innerHTML =
      swatch +
      '<div class="wishlist-row__body">' +
        '<a class="wishlist-row__name" href="' + url + '"></a>' +
        stock +
        noteControlHtml(variant.id, note) +
      '</div>' +
      '<span class="wishlist-row__price t-mono">' + money(variant.price) + '</span>' +
      '<div class="wishlist-row__actions">' + action +
        '<button type="button" class="wishlist-row__icon" data-wishlist-remove data-vid="' + variant.id + '" aria-label="' + bfT('remove', 'Entfernen') + '" title="' + bfT('remove', 'Entfernen') + '">♥</button>' +
        (loggedIn ? '<button type="button" class="wishlist-row__icon" data-wishlist-project data-vid="' + variant.id + '" data-handle="' + product.handle + '" aria-label="' + bfT('wl_add_to_project', 'In Projekt legen') + '" title="' + bfT('wl_add_to_project', 'In Projekt legen') + '">⋯</button>' : '') +
      '</div>';
    row.querySelector('.wishlist-row__name').textContent = variant.title && variant.title !== 'Default Title' ? variant.title : product.title;
    if (note) { var nt = row.querySelector('.wishlist-row__note-text'); if (nt) nt.textContent = note; }
    return row;
  }

  function emptyState(filtered) {
    var div = document.createElement('div');
    div.className = 'wishlist-empty';
    if (filtered) {
      div.innerHTML = '<p class="wishlist-empty__lead">' + bfT('wl_empty_filtered_lead', 'Keine verfügbaren Garne in dieser Ansicht.') + '</p>' +
        '<p class="wishlist-empty__hint t-mono">' + bfT('wl_empty_filtered_hint', 'Schalte „Nur verfügbare" aus, um auch ausverkaufte Farben zu sehen.') + '</p>';
      return div;
    }
    div.innerHTML =
      '<p class="wishlist-empty__lead">' + bfT('wl_nothing_saved', 'Noch nichts gemerkt.') + '</p>' +
      '<p class="wishlist-empty__hint t-mono">' + bfT('wl_empty_hint', 'Tippe auf das Herz an einem Garn, um es hier zu sammeln — jede Farbe einzeln.') + '</p>' +
      '<a class="btn btn--primary" href="/collections/all">' + bfT('discover_yarns', 'Garne entdecken') + '</a>';
    return div;
  }

  // Per-list description (logged-in only). Shows the active list's brief + an
  // edit affordance, or a "+ Beschreibung hinzufügen" prompt.
  function renderListDesc(root, state) {
    var slot = root.querySelector('[data-wishlist-listdesc]');
    if (!slot) return;
    if (!loggedIn) { slot.hidden = true; slot.innerHTML = ''; return; }
    var list = listById(state, activeListId) || listById(state, DEFAULT_ID);
    slot.hidden = false;
    if (list && list.desc) {
      slot.innerHTML = '<p class="wishlist__listdesc-text"></p>' +
        '<button type="button" class="wishlist__listdesc-edit" data-wishlist-listdesc-edit>' + bfT('edit', 'bearbeiten') + '</button>';
      slot.querySelector('.wishlist__listdesc-text').textContent = list.desc;
    } else {
      slot.innerHTML = '<button type="button" class="wishlist__listdesc-edit wishlist__listdesc-edit--add" data-wishlist-listdesc-edit>' + bfT('wl_add_desc', '+ Beschreibung hinzufügen') + '</button>';
    }
  }

  function renderNudge(root, state) {
    var slot = root.querySelector('[data-wishlist-nudge]');
    if (!slot) return;
    if (loggedIn) { slot.hidden = true; slot.innerHTML = ''; return; }
    if (localStorage.getItem('bf_wishlist_nudge_dismissed')) { slot.hidden = true; return; }
    if (slot.dataset.painted) return;
    slot.dataset.painted = '1';
    slot.hidden = false;
    slot.innerHTML = '<span>' + bfT('wl_signin_nudge', 'Melde dich an, damit deine Wunschliste auf allen Geräten bleibt — und du sie in Projekte sortieren kannst.') + '</span>' +
      '<a class="wishlist-nudge__cta" href="/account/login">' + bfT('signin', 'Anmelden') + '</a>' +
      '<button type="button" class="wishlist-nudge__close" data-wishlist-nudge-close aria-label="' + bfT('dismiss_hint', 'Hinweis schließen') + '">×</button>';
  }

  // ---- Cart add ----------------------------------------------------------

  function addToCart(vid, btn) {
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = bfT('adding', 'Wird hinzugefügt …');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }] })
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed');
      return res.json();
    }).then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('boni:cart:added', { detail: cart }));
        btn.textContent = bfT('added_check', 'Hinzugefügt ✓');
        setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1400);
      }).catch(function () {
        btn.textContent = bfT('failed', 'Fehlgeschlagen');
        setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 2000);
      });
  }

  // ---- List management (page) -------------------------------------------

  function createList() {
    openPrompt({
      title: bfT('wl_new_list_title', 'Neue Liste'), placeholder: bfT('wl_new_list_ph2', 'z. B. Pullover für Mama'), maxlength: 60, confirmLabel: bfT('create', 'Erstellen'),
      onConfirm: function (name) {
        if (!name) return;
        var s = loadState();
        if (s.lists.length >= MAX_LISTS) { showToast(bfT('max_lists', 'Maximale Anzahl an Listen erreicht.'), []); return; }
        var id = genListId();
        s.lists.push({ id: id, name: name.slice(0, 60), items: [] });
        saveState(s);
        activeListId = id;
        schedulePush();
        renderPage();
      }
    });
  }

  function renameActiveList() {
    var list = listById(loadState(), activeListId);
    if (!list || list.system) return;
    openPrompt({
      title: bfT('rename_list', 'Liste umbenennen'), value: list.name, maxlength: 60, confirmLabel: bfT('save', 'Speichern'),
      onConfirm: function (name) {
        if (!name) return;
        var s = loadState();
        var l = listById(s, activeListId);
        if (!l || l.system) return;
        l.name = name.slice(0, 60);
        saveState(s);
        schedulePush();
        renderPage();
      }
    });
  }

  function deleteActiveList() {
    var list = listById(loadState(), activeListId);
    if (!list || list.system) return;
    openConfirm({
      title: bfT('delete_list', 'Liste löschen'),
      message: bfT('wl_delete_msg', 'Liste „{name}" löschen? Die Garne bleiben in anderen Listen erhalten.').replace('{name}', list.name),
      confirmLabel: bfT('delete_btn', 'Löschen'), danger: true,
      onConfirm: function () {
        var s = loadState();
        s.lists = s.lists.filter(function (l) { return l.id !== activeListId; });
        activeListId = DEFAULT_ID;
        saveState(s);
        schedulePush();
        renderPage();
      }
    });
  }

  function toggleAllGroups() {
    var root = document.querySelector('[data-wishlist-root]');
    if (!root) return;
    var groups = root.querySelectorAll('[data-wishlist-group]');
    var anyOpen = false;
    groups.forEach(function (g) { if (!g.classList.contains('is-collapsed')) anyOpen = true; });
    var collapse = getCollapse();
    groups.forEach(function (g) {
      var handle = g.getAttribute('data-wishlist-group');
      if (anyOpen) { g.classList.add('is-collapsed'); collapse[handle] = 1; }
      else { g.classList.remove('is-collapsed'); delete collapse[handle]; }
      var head = g.querySelector('[data-wishlist-group-toggle]');
      if (head) head.setAttribute('aria-expanded', anyOpen ? 'false' : 'true');
    });
    setCollapse(collapse);
    var toggle = root.querySelector('[data-wishlist-toggle-all]');
    if (toggle) toggle.textContent = anyOpen ? 'Alle ausklappen' : 'Alle einklappen';
  }

  // ---- Delegated events --------------------------------------------------

  function bindEvents() {
    document.addEventListener('click', function (e) {
      var heart = e.target.closest('[data-wishlist-btn]');
      if (heart) { e.preventDefault(); toggleSave(heart); return; }

      var add = e.target.closest('[data-wishlist-add]');
      if (add) { addToCart(add.dataset.vid, add); return; }

      var remove = e.target.closest('[data-wishlist-remove]');
      if (remove) {
        var vid = Number(remove.dataset.vid);
        var s = loadState();
        removeEverywhere(s, vid);
        saveState(s);
        reflectVid(vid, false);
        paintBadge(allSavedVids(s).size);
        schedulePush();
        renderPage();
        return;
      }

      var proj = e.target.closest('[data-wishlist-project]');
      if (proj) { openProjectPicker(Number(proj.dataset.vid), proj.dataset.handle); return; }

      var noteBtn = e.target.closest('[data-wishlist-note]');
      if (noteBtn) {
        var nvid = Number(noteBtn.dataset.vid);
        var nlist = listById(loadState(), activeListId) || listById(loadState(), DEFAULT_ID);
        var nitem = nlist ? (nlist.items || []).filter(function (it) { return Number(it.vid) === nvid; })[0] : null;
        openPrompt({
          title: bfT('note_title', 'Notiz'), value: (nitem && nitem.note) ? nitem.note : '', multiline: true,
          placeholder: bfT('wl_note_ph', 'z. B. Größe M · 6 Knäuel'), maxlength: 500, confirmLabel: bfT('save', 'Speichern'),
          onConfirm: function (txt) {
            var s = loadState();
            var l = listById(s, activeListId) || listById(s, DEFAULT_ID);
            setItemNote(s, l ? l.id : DEFAULT_ID, nvid, txt);
            saveState(s);
            schedulePush();
            renderPage();
          }
        });
        return;
      }

      var descBtn = e.target.closest('[data-wishlist-listdesc-edit]');
      if (descBtn) {
        var dlist = listById(loadState(), activeListId) || listById(loadState(), DEFAULT_ID);
        openPrompt({
          title: bfT('wl_list_desc_title', 'Beschreibung der Liste'), value: (dlist && dlist.desc) ? dlist.desc : '', multiline: true,
          placeholder: bfT('wl_desc_ph', 'z. B. Geschenk für Mama, fertig bis Juni'), maxlength: 200, confirmLabel: bfT('save', 'Speichern'),
          onConfirm: function (txt) {
            var s = loadState();
            var l = listById(s, activeListId) || listById(s, DEFAULT_ID);
            setListDesc(s, l ? l.id : DEFAULT_ID, txt);
            saveState(s);
            schedulePush();
            renderPage();
          }
        });
        return;
      }

      var tab = e.target.closest('[data-wishlist-tab]');
      if (tab) { activeListId = tab.getAttribute('data-wishlist-tab'); renderPage(); return; }

      var newList = e.target.closest('[data-wishlist-newlist]');
      if (newList) { createList(); return; }

      var rename = e.target.closest('[data-wishlist-rename]');
      if (rename) { renameActiveList(); return; }

      var del = e.target.closest('[data-wishlist-delete]');
      if (del) { deleteActiveList(); return; }

      var toggleAll = e.target.closest('[data-wishlist-toggle-all]');
      if (toggleAll) { toggleAllGroups(); return; }

      var groupToggle = e.target.closest('[data-wishlist-group-toggle]');
      if (groupToggle) {
        var group = groupToggle.closest('[data-wishlist-group]');
        var handle = groupToggle.getAttribute('data-wishlist-group-toggle');
        var collapse = getCollapse();
        var nowCollapsed = !group.classList.contains('is-collapsed');
        group.classList.toggle('is-collapsed', nowCollapsed);
        groupToggle.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
        if (nowCollapsed) collapse[handle] = 1; else delete collapse[handle];
        setCollapse(collapse);
        return;
      }

      var nudgeClose = e.target.closest('[data-wishlist-nudge-close]');
      if (nudgeClose) {
        try { localStorage.setItem('bf_wishlist_nudge_dismissed', '1'); } catch (er) {}
        var slot = nudgeClose.closest('[data-wishlist-nudge]');
        if (slot) { slot.hidden = true; slot.innerHTML = ''; }
        return;
      }
    });

    document.addEventListener('change', function (e) {
      if (e.target.matches('[data-wishlist-instock]')) renderPage();
    });

    // PDP: keep the heart in step with the selected colorway.
    document.addEventListener('boni:variant:changed', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      var pdpBtn = document.querySelector('.wishlist-btn--pdp[data-wishlist-btn]');
      if (!pdpBtn) return;
      pdpBtn.dataset.vid = v.id;
      setHeart(pdpBtn, isSaved(loadState(), v.id));
    });
  }

  // ---- Boot --------------------------------------------------------------

  function boot() {
    bindEvents();

    var state = loadState();
    var hasLocalItems = allSavedVids(state).size > 0;

    if (loggedIn) {
      if (hasLocalItems && !localStorage.getItem(SYNCED_FLAG)) {
        var guestCount = allSavedVids(state).size;
        postMerge(state).then(function (merged) {
          if (merged) {
            saveState(merged);
            try { localStorage.setItem(SYNCED_FLAG, '1'); } catch (er) {}
            showToast((guestCount === 1 ? bfT('wl_guest_added_one', '{count} gemerktes Garn wurde deinem Konto hinzugefügt.') : bfT('wl_guest_added_other', '{count} gemerkte Garne wurden deinem Konto hinzugefügt.')).replace('{count}', guestCount), []);
          }
          refreshControls();
          if (isOnWishlistPage()) renderPage();
        });
      } else {
        // Logged in, nothing to merge → server is authoritative (cross-device).
        try { localStorage.setItem(SYNCED_FLAG, '1'); } catch (er) {}
        getServer().then(function (server) {
          if (server) saveState(server);
          refreshControls();
          if (isOnWishlistPage()) renderPage();
        });
      }
    } else {
      refreshControls();
      if (isOnWishlistPage()) renderPage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
